import { Loader2, CheckCircle, AlertTriangle, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { Correction } from '@/hooks/useProofreader';

interface ProofreadingIndicatorProps {
  isChecking: boolean;
  hasErrors: boolean;
  corrections: Correction[];
  onApplyCorrection: (correction: Correction) => void;
  onApplyAll: () => void;
}

export function ProofreadingIndicator({
  isChecking,
  hasErrors,
  corrections,
  onApplyCorrection,
  onApplyAll,
}: ProofreadingIndicatorProps) {
  if (isChecking) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
        <Loader2 className="h-3 w-3 animate-spin" />
        <span>Athuga stafsetningu...</span>
      </div>
    );
  }

  if (!hasErrors && corrections.length === 0) {
    return null;
  }

  if (!hasErrors) {
    return (
      <div className="flex items-center gap-2 text-sm text-green-600 mt-1">
        <CheckCircle className="h-3 w-3" />
        <span>Stafsetning í lagi</span>
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium text-amber-800">
          <AlertTriangle className="h-4 w-4" />
          <span>{corrections.length} {corrections.length === 1 ? 'villa' : 'villur'} fundust</span>
        </div>
        {corrections.length > 1 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onApplyAll}
            className="h-7 text-xs text-amber-700 hover:text-amber-900 hover:bg-amber-100"
          >
            <Sparkles className="h-3 w-3 mr-1" />
            Laga allt ({corrections.length})
          </Button>
        )}
      </div>

      <div className="space-y-1.5">
        {corrections.map((c, idx) => (
          <div
            key={idx}
            className="flex items-center justify-between gap-2 text-sm bg-white rounded px-2 py-1.5 border border-amber-100"
          >
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span className="text-xs text-gray-500 shrink-0">{c.label}</span>
              <span className="text-red-600 line-through truncate">{c.original}</span>
              <span className="text-gray-400">→</span>
              <span className="text-green-700 font-medium truncate">{c.suggestion}</span>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onApplyCorrection(c)}
              className="h-6 text-xs shrink-0"
            >
              Laga
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
